import React, { createContext, useContext, ReactNode } from 'react';
import { businessInfo } from '../../data/businessInfo';

interface LocalSEOContextType {
  businessInfo: typeof businessInfo;
  formatAddress: () => string;
  formatPhone: (phone: string) => string;
  generateLocalTitle: (pageTitle: string) => string;
  generateLocalDescription: (baseDescription: string) => string;
}

const LocalSEOContext = createContext<LocalSEOContextType | undefined>(undefined);

interface LocalSEOProviderProps {
  children: ReactNode;
}

export const LocalSEOProvider: React.FC<LocalSEOProviderProps> = ({ children }) => {
  const formatAddress = () => {
    const { address } = businessInfo;
    return `${address.streetAddress}, ${address.addressLocality}, ${address.addressRegion} ${address.postalCode}, ${address.addressCountry}`;
  };

  const formatPhone = (phone: string) => {
    // Remove all non-digits
    const cleaned = phone.replace(/\D/g, '');
    
    // Format as +91 XXXXX XXXXX for Indian numbers
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    
    return phone;
  };

  const generateLocalTitle = (pageTitle: string) => {
    return `${pageTitle} | ${businessInfo.name} - ${businessInfo.address.addressLocality}`;
  };

  const generateLocalDescription = (baseDescription: string) => {
    return `${baseDescription} Located in ${businessInfo.address.addressLocality}, serving ${businessInfo.areaServed.join(', ')}. Contact us at ${businessInfo.phone}.`;
  };

  const value: LocalSEOContextType = {
    businessInfo,
    formatAddress,
    formatPhone,
    generateLocalTitle,
    generateLocalDescription
  };

  return (
    <LocalSEOContext.Provider value={value}>
      {children}
    </LocalSEOContext.Provider>
  );
};

export const useLocalSEO = () => {
  const context = useContext(LocalSEOContext);
  if (context === undefined) {
    throw new Error('useLocalSEO must be used within a LocalSEOProvider');
  }
  return context;
};